import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getWaterLocational,
  getWaterSymbiotic,
  addWaterToBusiness,
  updateWater,
  deleteWater,
} from "../../../../../../apis/Business/EnergyApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const WaterLocationTable = ({ businessId, category, subcategory }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const DROPDOWN_CONFIGS = useMemo(
    () => ({
      regular: [
        {
          id: 1,
          name: t("WASTE_WATER"),
          value: t("WASTE_WATER"),
          type: t("WASTE_WATER"),
          unit: "kg",
          typeDB: "waste_water",
          key: "WASTE_WATER",
        },
        {
          id: 2,
          name: t("RAIN_WATER"),
          value: t("RAIN_WATER"),
          type: t("RAIN_WATER"),
          unit: "L",
          typeDB: "rain_water",
          key: "RAIN_WATER",
        },
        {
          id: 3,
          name: t("MELT_WATER"),
          value: t("MELT_WATER"),
          type: t("MELT_WATER"),
          unit: "L",
          typeDB: "melt_water",
          key: "MELT_WATER",
        },
        {
          id: 4,
          name: t("DRAINAGE_WATER"),
          value: t("DRAINAGE_WATER"),
          type: t("DRAINAGE_WATER"),
          unit: "L",
          typeDB: "drainage_water",
          key: "DRAINAGE_WATER",
        },
        {
          id: 4,
          name: t("WELL_WATER"),
          value: t("WELL_WATER"),
          type: t("WELL_WATER"),
          unit: "L",
          typeDB: "well_water",
          key: "WELL_WATER",
        },
      ],
      symbiotic: [
        {
          id: 1,
          name: t("DRINKING_WATER_EUROPE"),
          value: t("DRINKING_WATER_EUROPE"),
          type: t("DRINKING_WATER_EUROPE"),
          unit: "kg",
          typeDB: "europe_drinking_water",
          key: "DRINKING_WATER_EUROPE",
        },
        {
          id: 2,
          name: t("INDUSTRIAL_REVERSE_OSMOSIS"),
          value: t("INDUSTRIAL_REVERSE_OSMOSIS"),
          type: t("INDUSTRIAL_REVERSE_OSMOSIS"),
          unit: "kg",
          typeDB: "osmosis",
          key: "INDUSTRIAL_REVERSE_OSMOSIS",
        },
      ],
    }),
    [t]
  );

  const [dropdownUsedFor, setDropdownUsedFor] = useState(
    DROPDOWN_CONFIGS[category === "symbiotic" ? "symbiotic" : "regular"]
  );

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  useEffect(() => {
    setDropdownUsedFor(
      DROPDOWN_CONFIGS[category === "symbiotic" ? "symbiotic" : "regular"]
    );
  }, [category, t, DROPDOWN_CONFIGS]);

  const fetchData = useCallback(() => {
    category === "found"
      ? getWaterLocational({
          businessId,
          setObjects: setData,
        })
      : getWaterSymbiotic({
          businessId,
          setObjects: setData,
        });
  }, [businessId, category]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["type_water.value"]?.value || row.type_water?.value;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "type_water.value": {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "type_water.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      let typeObj = {};
      if (editedItem["type_water.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type_water.value"].value
        );
      } else if (editedItem["type_water"].value) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type_water"].value
        );
      }

      const typedb =
        typeObj && typeObj.typeDB
          ? typeObj.typeDB
          : editedItem.type && typeof editedItem.type === "string"
          ? editedItem.type
          : data["typedb_water"];
      updateWater({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        category: category,
        subcategory: subcategory,
        materials: null,
        typeWater: editedItem["type_water.value"]
          ? editedItem["type_water.value"].value
          : editedItem["type_water"]
          ? editedItem["type_water"].value
          : null,
        typeDBWater: typedb,
        isSubstitute: editedItem.isSubstitute,
        objects: data,
        setObjects: setData,
        use_swal: true,
        type_equipment: null,
        material: null,
        invoiceDate: editedItem["invoice_date"],
        invoiceNo: editedItem["invoice_number"],
      });
    },
    [businessId, dropdownUsedFor, data, setData, category, subcategory]
  );

  const handleAdd = useCallback(
    (newItem) => {
      let typeObj = {};
      if (newItem["type_water.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === newItem["type_water.value"].value
        );
      }
      addWaterToBusiness({
        businessId,
        ...newItem,
        category: category,
        subcategory: subcategory,
        typeWater: newItem["type_water.value"]
          ? newItem["type_water.value"].value
          : null,
        typeDBWater: typeObj ? typeObj.typeDB : newItem.type,
        isSubstitute: newItem.isSubstitute,
        materials: null,
        callback: fetchData,
        use_swal: true,
        type_equipment: null,
        material: null,
        invoiceDate: newItem["invoice_date"],
        invoiceNo: newItem["invoice_number"],
      });
    },
    [businessId, dropdownUsedFor, fetchData, category, subcategory]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteWater({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "type_water.value",
        header: `${t("TYPE_WATER")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "nickname",
        header: t("WATER_SUPPLY_NAME"),
        type: "text",
        disableSortBy: true,
        placeholder: "0",
        width: "110px",
      },
      {
        accessor: "qty",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("QUANTITY")}* (${unitMaterials[rowId] || "xx"})`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "110px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.type_water?.value || rowData?.["type_water.value"]?.value;
          return rowType
            ? dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId] || "xx";
        },
      },
      {
        accessor: "isSubstitute",
        header: t("IS_SUBSTITUTE_WATER"),
        type: "switch",
        disableSortBy: true,
        width: "120px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
    ],
    [dropdownUsedFor, t, unitMaterials]
  );

  const subtotalColumns = {};

  const memoizedDropdownData = useMemo(
    () => ({
      "type_water.value": dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      subtotalColumns={subtotalColumns}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default WaterLocationTable;
